export type QlsMarker =
  | { kind: 'advanced'; marker: google.maps.marker.AdvancedMarkerElement }
  | { kind: 'legacy'; marker: google.maps.Marker };

export function clearMarker(m: QlsMarker | null | undefined) {
  if (!m) return;
  if (m.kind === 'advanced') {
    m.marker.map = null;
  } else {
    m.marker.setMap(null);
  }
}

export function openInfoWindow(params: {
  infoWindow: google.maps.InfoWindow;
  map: google.maps.Map;
  anchor: QlsMarker;
}) {
  const { infoWindow, map, anchor } = params;
  if (anchor.kind === 'advanced') {
    infoWindow.open({ map, anchor: anchor.marker });
  } else {
    infoWindow.open(map, anchor.marker);
  }
}

export function addMarkerClickListener(params: {
  marker: QlsMarker;
  handler: (event?: unknown) => void;
}) {
  const { marker, handler } = params;
  if (marker.kind === 'advanced') {
    // AdvancedMarkerElement uses DOM-style events and "gmp-click"
    marker.marker.addEventListener('gmp-click', handler);
    return;
  }

  marker.marker.addListener('click', handler);
}

function createPinContent(params: {
  sizePx?: number;
  fillColor: string;
  strokeColor?: string;
  strokeWidthPx?: number;
  labelText?: string;
  labelColor?: string;
  labelFontSizePx?: number;
  labelFontWeight?: string;
}) {
  const {
    sizePx = 20,
    fillColor,
    strokeColor = '#ffffff',
    strokeWidthPx = 3,
    labelText,
    labelColor = '#ffffff',
    labelFontSizePx = 12,
    labelFontWeight = '700',
  } = params;

  const el = document.createElement('div');
  el.style.width = `${sizePx}px`;
  el.style.height = `${sizePx}px`;
  el.style.borderRadius = '9999px';
  el.style.backgroundColor = fillColor;
  el.style.border = `${strokeWidthPx}px solid ${strokeColor}`;
  el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.userSelect = 'none';

  if (labelText) {
    const label = document.createElement('div');
    label.textContent = labelText;
    label.style.color = labelColor;
    label.style.fontSize = `${labelFontSizePx}px`;
    label.style.fontWeight = labelFontWeight;
    label.style.lineHeight = '1';
    el.appendChild(label);
  }

  return el;
}

export function createQlsMarker(params: {
  map: google.maps.Map;
  mapId?: string;
  position: google.maps.LatLngLiteral;
  title?: string;
  /** For advanced markers, this is mapped to `gmpDraggable`. */
  draggable?: boolean;
  /** Circle pin configuration. */
  pin?: {
    fillColor: string;
    sizePx?: number;
    strokeColor?: string;
    strokeWidthPx?: number;
    labelText?: string;
    labelColor?: string;
    labelFontSizePx?: number;
    labelFontWeight?: string;
  };
  /** Custom DOM content for Advanced Marker. If provided, overrides `pin`. */
  content?: HTMLElement;
}) : QlsMarker {
  const { map, mapId, position, title, draggable, pin, content } = params;

  if (mapId) {
    const markerContent =
      content ??
      (pin
        ? createPinContent({
            sizePx: pin.sizePx,
            fillColor: pin.fillColor,
            strokeColor: pin.strokeColor,
            strokeWidthPx: pin.strokeWidthPx,
            labelText: pin.labelText,
            labelColor: pin.labelColor,
            labelFontSizePx: pin.labelFontSizePx,
            labelFontWeight: pin.labelFontWeight,
          })
        : undefined);

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      title,
      content: markerContent,
    });

    if (typeof draggable === 'boolean') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (marker as any).gmpDraggable = draggable;
    }

    return { kind: 'advanced', marker };
  }

  const legacyMarker = new google.maps.Marker({
    map,
    position,
    title,
    draggable,
    ...(pin
      ? {
          label: pin.labelText
            ? {
                text: pin.labelText,
                color: pin.labelColor ?? '#ffffff',
                fontSize: pin.labelFontSizePx ? `${pin.labelFontSizePx}px` : undefined,
                fontWeight: pin.labelFontWeight ?? '700',
              }
            : undefined,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: typeof pin.sizePx === 'number' ? Math.max(4, Math.round(pin.sizePx / 2)) : 8,
            fillColor: pin.fillColor,
            fillOpacity: 1,
            strokeColor: pin.strokeColor ?? '#ffffff',
            strokeWeight: pin.strokeWidthPx ?? 2,
          },
        }
      : {}),
  });

  return { kind: 'legacy', marker: legacyMarker };
}

